import { ReactNode } from "react";
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { ClassesIcon } from "./navIcons";
import { Sheet } from "../primitives/Sheet";
import { focusRing, focusRingOnInk } from "@ui/tokens/interaction";
import type { NavClass, NavEntry } from "./NavMenu";
import { ClassRail } from "../business/ClassRail";

/** 36px visible, 44px hit area — the same extension used since step 2. */
const roundButton =
  "relative inline-flex items-center justify-center shrink-0 w-mobile-button h-mobile-button rounded-pill transition duration-hover active:scale-95 after:absolute after:left-1/2 after:top-1/2 after:w-touch after:h-touch after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";

const secondaryButton = `${roundButton} bg-surface-content text-ink-mobile-button shadow-swatch-neutral-ring hover:text-ink`;

interface MobileTopBarProps {
  onSearch?: () => void;
  onMenu: () => void;
  onAdd: () => void;
  menuOpen?: boolean;
}

export const MobileTopBar = ({ onSearch, onMenu, onAdd, menuOpen = false }: MobileTopBarProps) => (
  <header className="flex items-center justify-between shrink-0 gap-2.5 px-4 py-3.25 bg-surface-bar border-b border-line">
    <span className="inline-flex items-center gap-2.25 min-w-0">
      <span className="flex items-center justify-center shrink-0 w-hero-btn h-hero-btn rounded-row bg-signature text-on-create text-overlay-title">
        N
      </span>
      <span className="text-overlay-title text-ink">Nablow</span>
    </span>

    <span className="flex items-center shrink-0 gap-1.75">
      <button
        type="button"
        onClick={onSearch}
        aria-label="Rechercher"
        className={`${secondaryButton} ${focusRing}`}
      >
        <MagnifyingGlassIcon className="w-4.25 h-4.25" strokeWidth={2.1} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onMenu}
        aria-label="Navigation"
        aria-expanded={menuOpen}
        className={`${roundButton} ${
          menuOpen
            ? `bg-action text-on-action shadow-nav-active ${focusRingOnInk}`
            : `bg-surface-content text-ink-mobile-button shadow-swatch-neutral-ring hover:text-ink ${focusRing}`
        }`}
      >
        <Bars3Icon className="w-4.25 h-4.25" strokeWidth={2.4} aria-hidden="true" />
      </button>
      {/* The single ink control of the bar — the mobile counterpart of "Ajouter". */}
      <button
        type="button"
        onClick={onAdd}
        aria-label="Ajouter"
        className={`${roundButton} bg-mobile-action text-white shadow-nav-active ${focusRingOnInk}`}
      >
        <PlusIcon className="w-4.25 h-4.25" strokeWidth={2.4} aria-hidden="true" />
      </button>
    </span>
  </header>
);

interface MobileFabProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

/** One primary action per screen: a 52px circle, pink rule, no label. */
export const MobileFab = ({ label, icon, onClick }: MobileFabProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={`fixed bottom-5 left-1/2 -translate-x-1/2 z-40 inline-flex items-center justify-center w-mobile-fab h-mobile-fab rounded-pill bg-fab text-white shadow-fab ${focusRingOnInk}`}
  >
    {icon}
  </button>
);

interface MobileNavSheetProps {
  isOpen: boolean;
  onClose: () => void;
  entries: readonly NavEntry[];
  classes: readonly NavClass[];
  activeId: string | null;
  activeClassId: string | null;
  onSelect: (id: string) => void;
  onSelectClass: (id: string) => void;
}

/** Navigation is a bottom sheet, not a tab bar — the reference has no tab bar. */
export const MobileNavSheet = ({
  isOpen,
  onClose,
  entries,
  classes,
  activeId,
  activeClassId,
  onSelect,
  onSelectClass,
}: MobileNavSheetProps) => (
  <Sheet isOpen={isOpen} onClose={onClose} title="Navigation">
    <div className="flex flex-col gap-0.5 pt-3">
      {entries.map((entry) => (
        <button
          key={entry.id}
          type="button"
          onClick={() => {
            onSelect(entry.id);
            onClose();
          }}
          disabled={entry.disabled}
          aria-current={activeId === entry.id ? "page" : undefined}
          className={`flex items-center gap-3.25 min-h-touch px-3.5 rounded-field text-left text-control-lg transition duration-hover ${
            entry.disabled
              ? "text-ink-meta-faint"
              : activeId === entry.id
                ? `bg-action text-on-action-accent font-700 shadow-nav-active-rule ${focusRingOnInk}`
                : `text-ink-body font-600 hover:bg-row-hover ${focusRing}`
          }`}
        >
          {entry.icon}
          {entry.label}
        </button>
      ))}

      <span className="inline-flex items-center gap-3.25 px-3.5 pt-3 pb-1.25 text-section-label uppercase text-ink-meta">
        <ClassesIcon />
        Mes classes
      </span>

      {classes.map(({ id, name, cover }) => {
        const active = activeClassId === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => {
              onSelectClass(id);
              onClose();
            }}
            aria-current={active ? "page" : undefined}
            style={active ? { color: cover.c } : undefined}
            className={`flex items-center gap-2.75 min-h-touch px-3.5 rounded-field text-left text-body font-600 transition duration-hover ${
              active ? `bg-class-active ${focusRingOnInk}` : `text-ink-body hover:bg-row-hover ${focusRing}`
            }`}
          >
            <ClassRail cover={cover} height={18} />
            {name}
          </button>
        );
      })}
    </div>
  </Sheet>
);

interface MobileShellProps {
  topBar: ReactNode;
  children: ReactNode;
}

/**
 * The tinted band runs full width and all the way down — never a cut in the middle of empty
 * space. Cards sit white on top of it, with a real shadow: on mobile the separation comes from
 * the background value, not from a hairline.
 *
 * `main` is itself a flex column, not a plain block: a screen that wants its own content to reach
 * the bottom of the page (Storage's white surface, for instance) needs `flex-1` to resolve against
 * something, and a flex item's percentage-height children are the one case real mobile browsers
 * are known to get wrong. Flex-grow the whole way down sidesteps that rather than relying on it.
 */
export const MobileShell = ({ topBar, children }: MobileShellProps) => (
  <div className="flex flex-col min-h-screen bg-surface-page-mobile font-ui">
    {topBar}
    <main className="flex flex-col flex-1 min-h-0 px-3.5 py-3.5 pb-24">{children}</main>
  </div>
);
